// Sales Planner: pure helpers (no React), tested by
// frontend/scripts/planner.test.mjs. All dates are the viewer's LOCAL time.

export const REP_COLORS = ['#60A5FA', '#FB923C', '#2DD4BF', '#C084FC', '#F472B6', '#FACC15', '#A3E635', '#F87171'];
export const repColor = (index) => REP_COLORS[((index % REP_COLORS.length) + REP_COLORS.length) % REP_COLORS.length];

export const DEFAULT_TARGET = 8;
export const DEFAULT_HOURS = { start: 7, end: 18 };
export const DEFAULT_INCLUDE = { visit: true, call: true, other: true, planned: true, email: false };

// --- Dates -----------------------------------------------------------------
const pad = (n) => String(n).padStart(2, '0');
export const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const sameDay = (a, b) => dayKey(a) === dayKey(b);
// Weeks start on Monday.
export const startOfWeek = (d) => addDays(startOfDay(d), -((d.getDay() + 6) % 7));
export const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

// The Monday-to-Sunday weeks that cover the month of `d`.
export const monthWeeks = (d) => {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const weeks = [];
  for (let w = startOfWeek(first); w <= last; w = addDays(w, 7)) {
    weeks.push([0, 1, 2, 3, 4, 5, 6].map(i => addDays(w, i)));
  }
  return weeks;
};

// What to fetch for a view: the whole week for Day and Week (the summary
// cards count the week), the covering weeks for Month. `to` is excluded.
export const rangeFor = (view, d) => {
  if (view === 'month') {
    const weeks = monthWeeks(d);
    return { from: weeks[0][0], to: addDays(weeks[weeks.length - 1][6], 1) };
  }
  const from = startOfWeek(d);
  return { from, to: addDays(from, 7) };
};

export const shiftDate = (view, d, step) => {
  if (view === 'day') return addDays(d, step);
  if (view === 'week') return addDays(d, 7 * step);
  return new Date(d.getFullYear(), d.getMonth() + step, 1);
};

export const fmtTime = (minutes) => {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${pad(mm)} ${h >= 12 ? 'PM' : 'AM'}`;
};
export const fmtHour = (h) => fmtTime(h * 60).replace(':00', '');
export const fmtLength = (minutes) => {
  if (!minutes) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
};

export const rangeLabel = (view, d) => {
  if (view === 'day') return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  if (view === 'month') return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const from = startOfWeek(d);
  const to = addDays(from, 6);
  const f = from.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const t = to.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${f} – ${t}`;
};

// --- Activities ------------------------------------------------------------
// The contact method an activity type implies. Picking the type fills it in;
// it can still be changed. Types not listed leave the method alone.
export const METHOD_FOR_TYPE = { call: 'phone', email: 'email', visit: 'in_person', proposal_shown: 'in_person' };

// What kind of contact it was. Clark cares about in-person and phone. A clear
// type wins over the method, because the method used to stay on Phone when a
// rep changed the type to "Visited client". Keep in sync with
// is_in_person in backend/planner_logic.py.
export const kindOf = (a) => {
  const t = a?.activity_type;
  if (t === 'visit') return 'visit';
  if (t === 'call') return 'call';
  if (t === 'email') return 'email';
  const m = a?.contact_method;
  if (m === 'in_person' || m === 'meeting') return 'visit';
  if (m === 'phone') return 'call';
  if (m === 'email') return 'email';
  return 'other';
};

export const isInPerson = (activityType, contactMethod) =>
  kindOf({ activity_type: activityType, contact_method: contactMethod }) === 'visit';

// A rep is on an activity as its owner or as the rep who went along.
export const onRep = (a, repId) => !!repId && (a?.sales_rep_id === repId || a?.accompanied_by_rep_id === repId);

// The other rep on a meeting, seen from repId's column: "Robyn" for a block in
// Crystal's column. With no repId (the Week view), the one who went along.
export const otherRepName = (a, repId) => {
  if (!a?.accompanied_by_rep_id) return '';
  const name = repId && repId === a.accompanied_by_rep_id ? a.sales_rep_name : a.accompanied_by_name;
  return (name || '').trim().split(/\s+/)[0] || '';
};

export const KIND_LABELS = { visit: 'In-person meeting', call: 'Phone call', email: 'Email', other: 'Other contact' };

// Adds what the page needs to each activity from /sales/planner:
//   kind, start (Date), day (YYYY-MM-DD), startMin, length (minutes),
//   timed (has a real time of day), state: done | planned | missed
// "missed" = planned, and its end has passed with nothing logged.
export const prepare = (activities, now) => (activities || []).map(a => {
  const start = new Date(a.occurred_at);
  const timed = !!a.duration_minutes;
  const length = timed ? a.duration_minutes : 0;
  let state = 'done';
  if (a.status === 'planned') {
    const end = new Date(start.getTime() + Math.max(length, 15) * 60000);
    state = end < now ? 'missed' : 'planned';
  }
  return {
    ...a,
    kind: kindOf(a),
    start,
    day: dayKey(start),
    startMin: start.getHours() * 60 + start.getMinutes(),
    length,
    timed,
    state,
  };
}).filter(a => !Number.isNaN(a.start.getTime()));

export const isVisible = (item, include) => {
  const inc = include || DEFAULT_INCLUDE;
  if (!inc[item.kind]) return false;
  if (item.state !== 'done' && !inc.planned) return false;
  return true;
};

// Columns for the Day view / the reps a view covers. Reps that logged
// something but have no rep record (logged by a user with no matching rep)
// are added from the activity so nothing disappears.
// show: 'all' | 'me' | a rep id
export const repsFor = (reps, items, show, myRepId) => {
  const list = (reps || []).map((r, i) => ({
    id: r.id, name: r.name, target: Number.isInteger(r.weekly_meeting_target) ? r.weekly_meeting_target : DEFAULT_TARGET, color: repColor(i),
  }));
  const known = new Set(list.map(r => r.id));
  (items || []).forEach(a => {
    if (a.sales_rep_id && !known.has(a.sales_rep_id)) {
      known.add(a.sales_rep_id);
      list.push({ id: a.sales_rep_id, name: a.sales_rep_name || 'Unknown', target: 0, color: repColor(list.length), unlisted: true });
    }
  });
  if (show === 'me') return list.filter(r => myRepId && r.id === myRepId);
  if (show && show !== 'all') return list.filter(r => r.id === show);
  return list;
};

// Counts for one rep between two dates (to excluded). Only logged
// activities count; emails are counted but never treated as meetings. A rep
// who went along gets the meeting; the sale and any unlogged meeting stay with
// the rep who owns it.
export const repStats = (items, repId, from, to) => {
  const s = { meet: 0, calls: 0, emails: 0, other: 0, won: 0, planned: 0, missed: 0 };
  (items || []).forEach(a => {
    if (!onRep(a, repId) || a.start < from || a.start >= to) return;
    const along = a.sales_rep_id !== repId;
    if (a.state === 'planned') { s.planned += 1; return; }
    if (a.state === 'missed') { if (!along) s.missed += 1; return; }
    if (along) { if (a.kind === 'visit') s.meet += 1; return; }
    if (a.kind === 'visit') s.meet += 1;
    else if (a.kind === 'call') s.calls += 1;
    else if (a.kind === 'email') s.emails += 1;
    else s.other += 1;
    if (a.outcome === 'won') s.won += 1;
  });
  return s;
};

// Weekdays (Mon-Fri) of the week starting `weekStart` that have started by
// `now`, today included: 0 for a future week, 5 for a past one.
export const workdaysElapsed = (weekStart, now) => {
  let n = 0;
  for (let i = 0; i < 5; i += 1) {
    if (addDays(weekStart, i) <= now) n += 1;
  }
  return n;
};

// none: no target. met: target reached. on: keeping up so far.
// behind: fewer meetings than the target spread over the weekdays so far.
export const pace = (meet, target, weekStart, now) => {
  if (!target) return 'none';
  if (meet >= target) return 'met';
  const expected = Math.floor((target * workdaysElapsed(weekStart, now)) / 5);
  return meet < expected ? 'behind' : 'on';
};

// Which weekend days to show: Saturday and Sunday only when something is on
// them (in the dates given).
export const weekendShown = (items, dates) => {
  const keys = new Set((items || []).map(a => a.day));
  const show = { 6: false, 0: false };
  (dates || []).forEach(d => {
    if (isWeekend(d) && keys.has(dayKey(d))) show[d.getDay()] = true;
  });
  return show;
};

export const visibleDays = (dates, shown) => dates.filter(d => !isWeekend(d) || shown[d.getDay()]);

// The hours the grid covers: 7 AM to 6 PM, stretched to fit anything earlier
// or later.
export const hourRange = (timedItems, defaults = DEFAULT_HOURS) => {
  let start = defaults.start;
  let end = defaults.end;
  (timedItems || []).forEach(a => {
    start = Math.min(start, Math.floor(a.startMin / 60));
    end = Math.max(end, Math.ceil((a.startMin + Math.max(a.length, 15)) / 60));
  });
  return { start: Math.max(0, start), end: Math.min(24, end) };
};

// Side-by-side lanes for blocks that overlap in one column. Returns new
// objects with lane (0-based) and lanes (how many share that cluster).
export const layoutLanes = (items) => {
  const sorted = [...(items || [])].sort((a, b) => a.startMin - b.startMin || b.length - a.length);
  const out = [];
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = cluster.reduce((m, x) => Math.max(m, x.lane + 1), 0);
    cluster.forEach(x => out.push({ ...x, lanes }));
    cluster = [];
  };
  sorted.forEach(a => {
    const end = a.startMin + Math.max(a.length, 15);
    if (cluster.length && a.startMin >= clusterEnd) { flush(); clusterEnd = -1; }
    const used = new Set(cluster.filter(x => x._end > a.startMin).map(x => x.lane));
    let lane = 0;
    while (used.has(lane)) lane += 1;
    cluster.push({ ...a, lane, _end: end });
    clusterEnd = Math.max(clusterEnd, end);
  });
  if (cluster.length) flush();
  return out.map(({ _end, ...rest }) => rest);
};

// Where a block sits on the grid, in pixels.
export const blockBox = (item, startHour, pxPerHour) => ({
  top: ((item.startMin - startHour * 60) / 60) * pxPerHour,
  height: Math.max((Math.max(item.length, 15) / 60) * pxPerHour, 22),
});

// Saved view settings (per user, per browser). Bad or missing -> defaults.
export const readPrefs = (raw) => {
  const out = { view: 'day', show: 'all', include: { ...DEFAULT_INCLUDE } };
  try {
    const p = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
    if (['day', 'week', 'month'].includes(p.view)) out.view = p.view;
    if (typeof p.show === 'string' && p.show) out.show = p.show;
    if (p.include && typeof p.include === 'object') {
      Object.keys(DEFAULT_INCLUDE).forEach(k => {
        if (typeof p.include[k] === 'boolean') out.include[k] = p.include[k];
      });
    }
  } catch (e) { /* keep defaults */ }
  return out;
};
